<template>
  <div class="container box_component">
    <div class="header header-sticky" :class="{ 'header-hidden-mobile': hideMobileHeader }">
      <div>
        <p class="eyebrow">{{ $t('competitions.assistant.eyebrow') }}</p>
        <h2>{{ $t('competitions.assistant.title') }}</h2>
        <p class="lead">
          {{ $t('competitions.assistant.subtitle') }}
        </p>
      </div>
      <div class="progress-wrapper">
        <div class="progress-label">{{ $t('competitions.assistant.progress') }} {{ progress }}%</div>
        <div class="progress-bar-outer">
          <div class="progress-bar-inner" :style="{ width: progress + '%' }"></div>
        </div>
        <ul class="checklist">
          <li v-for="item in checklist" :key="item.key" :class="{ done: item.done }">
            <i :class="item.done ? 'fa-solid fa-check-circle' : 'fa-regular fa-circle'"></i>
            <span>{{ item.label }}</span>
          </li>
        </ul>
      </div>
    </div>

    <section class="card">
      <div class="card-header">
        <h3>{{ $t('competitions.assistant.card_basic_title') }}</h3>
        <p class="card-sub">{{ $t('competitions.assistant.card_basic_subtitle') }}</p>
      </div>
      <div class="card-body">
        <div class="field">
          <label>{{ $t('competitions.assistant.labels.name') }}</label>
          <input v-model="form.name" type="text" :placeholder="$t('competitions.assistant.placeholders.name')" @input="persist" />
        </div>

        <transition name="fade-step">
          <div v-if="canShowCountry" class="field">
            <label>{{ $t('competitions.assistant.labels.country') }}</label>
            <select v-model="form.country_id" @change="persist">
              <option :value="null">{{ $t('competitions.assistant.placeholders.country') }}</option>
              <option v-for="country in countries" :key="country.id" :value="country.id">
                {{ country.name }}
              </option>
            </select>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowType" class="field">
            <label>{{ $t('competitions.assistant.labels.type') }}</label>
            <div class="type-grid">
              <div
                  v-for="option in typeOptions"
                  :key="option.id"
                  class="type-card"
                  :class="{ active: form.type_id === option.id, disabled: option.disabled }"
                  @click="selectType(option.id)"
              >
                <div class="type-header">
                  <span class="pill">{{ option.label }}</span>
                  <i v-if="form.type_id === option.id" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.type_id === option.id">{{ option.description }}</p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowDates" class="field-grid">
            <div class="field">
              <label>{{ $t('competitions.assistant.labels.start') }}</label>
              <VueDatePicker
                  v-model="startDateTime"
                  :enable-time-picker="true"
                  :minute-increment="5"
                  :is-24="true"
                  :teleport="true"
                  :flow="['calendar']"
                  :format="'dd/MM/yyyy HH:mm'"
                  :min-date="minStartDate"
                  @update:model-value="onStartChange"
              />
            </div>
            <div v-if="!isOnline" class="field">
              <label>{{ $t('competitions.assistant.labels.end') }}</label>
              <VueDatePicker
                  v-model="endDateTime"
                  :enable-time-picker="true"
                  :minute-increment="5"
                  :is-24="true"
                  :teleport="true"
                  :flow="['calendar']"
                  :format="'dd/MM/yyyy HH:mm'"
                  :disabled="!canEnableEnd"
                  :min-date="minStartDate"
                  @update:model-value="onEndChange"
              />
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowFlowScope" class="field">
            <label>{{ $t('competitions.assistant.labels.flow_scope') }}</label>
            <div class="type-grid">
                <div
                    class="type-card"
                    :class="{ active: form.flow_scope === 'qual_only' }"
                    @click="selectFlowScope('qual_only')"
                >
                  <div class="type-header">
                    <span class="pill">{{ $t('competitions.assistant.labels.flow_scope_qual_only') }}</span>
                    <i v-if="form.flow_scope === 'qual_only'" class="fa-solid fa-check"></i>
                  </div>
                  <p class="type-description" v-if="form.flow_scope === 'qual_only'">
                    {{ $t('competitions.assistant.labels.flow_scope_qual_only_desc') }}
                  </p>
                  <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
                </div>
                <div
                    class="type-card"
                    :class="{ active: form.flow_scope === 'full' }"
                    @click="selectFlowScope('full')"
                >
                  <div class="type-header">
                    <span class="pill">{{ $t('competitions.assistant.labels.flow_scope_full') }}</span>
                    <i v-if="form.flow_scope === 'full'" class="fa-solid fa-check"></i>
                  </div>
                  <p class="type-description" v-if="form.flow_scope === 'full'">
                    {{ $t('competitions.assistant.labels.flow_scope_full_desc') }}
                  </p>
                  <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
                </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowQualification" class="field">
            <label>{{ $t('competitions.assistant.labels.qualification') }}</label>
            <div class="type-grid">
              <div
                  class="type-card"
                  :class="{ active: form.is_qualification_dual === 0 }"
                  @click="selectQualification(0)"
              >
                <div class="type-header">
                  <span class="pill">{{ $t('competitions.assistant.labels.qualification_one') }}</span>
                  <i v-if="form.is_qualification_dual === 0" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.is_qualification_dual === 0">
                  {{ $t('competitions.assistant.labels.qualification_one_desc') }}
                </p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
              <div
                  class="type-card"
                  :class="{ active: form.is_qualification_dual === 1 }"
                  @click="selectQualification(1)"
              >
                <div class="type-header">
                  <span class="pill">{{ $t('competitions.assistant.labels.qualification_two') }}</span>
                  <i v-if="form.is_qualification_dual === 1" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.is_qualification_dual === 1">
                  {{ $t('competitions.assistant.labels.qualification_two_desc') }}
                </p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowMode" class="field">
            <label>{{ $t('competitions.assistant.labels.mode') }}</label>
            <div class="type-grid">
              <div
                  class="type-card"
                  :class="{ active: form.is_vs === 1 }"
                  @click="selectMode(1)"
              >
                <div class="type-header">
                  <span class="pill">{{ $t('competitions.assistant.labels.mode_vs') }}</span>
                  <i v-if="form.is_vs === 1" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.is_vs === 1">
                  {{ $t('competitions.assistant.labels.mode_vs_desc') }}
                </p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
              <div
                  class="type-card"
                  :class="{ active: form.is_vs === 0 }"
                  @click="selectMode(0)"
              >
                <div class="type-header">
                  <span class="pill">{{ $t('competitions.assistant.labels.mode_scoring') }}</span>
                  <i v-if="form.is_vs === 0" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.is_vs === 0">
                  {{ $t('competitions.assistant.labels.mode_scoring_desc') }}
                </p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowRule" class="field-grid field-grid-3">
            <div class="field">
              <label>{{ $t('competitions.assistant.labels.gender') }}</label>
              <select v-model="form.gender" @change="persist">
                <option :value="null">{{ $t('competitions.assistant.labels.select_hint') }}</option>
                <option v-for="gender in genders" :key="gender.value" :value="gender.value">{{ gender.name }}</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('competitions.assistant.labels.category') }}</label>
              <select v-model="form.category" @change="persist">
                <option :value="null">{{ $t('competitions.assistant.labels.select_hint') }}</option>
                <option
                    v-for="category in categories"
                    :key="category.value"
                    :value="category.value"
                    :disabled="category.value === 'team'"
                >
                  {{ category.name }}
                </option>
              </select>
            </div>
            <div class="field">
              <label class="d-flex align-items-center justify-content-between">
                <span>{{ $t('competitions.assistant.labels.rule') }}</span>
                <a
                    class="rule-link"
                    href="https://staging.slackline-tjs.com/rules"
                    target="_blank"
                    rel="noopener"
                    :title="$t('competitions.assistant.labels.rule_link_title')"
                >
                  <i class="fa-solid fa-circle-info"></i>
                </a>
              </label>
              <select v-model="form.rule_id" @change="persist">
                <option :value="null">{{ $t('competitions.assistant.labels.select_hint') }}</option>
                <option v-for="rule in rules" :key="rule.value" :value="rule.value">{{ rule.name }}</option>
              </select>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowOpen" class="field">
            <label>{{ $t('competitions.assistant.labels.access') }}</label>
            <div class="type-grid">
              <div
                  class="type-card"
                  :class="{ active: form.is_open === 1 }"
                  @click="selectOpen(1)"
              >
                <div class="type-header">
                  <span class="pill">{{ $t('competitions.assistant.labels.open') }}</span>
                  <i v-if="form.is_open === 1" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.is_open === 1">{{ $t('competitions.assistant.labels.open_desc') }}</p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
              <div
                  class="type-card"
                  :class="{ active: form.is_open === 0 }"
                  @click="selectOpen(0)"
              >
                <div class="type-header">
                  <span class="pill">{{ $t('competitions.assistant.labels.closed') }}</span>
                  <i v-if="form.is_open === 0" class="fa-solid fa-check"></i>
                </div>
                <p class="type-description" v-if="form.is_open === 0">{{ $t('competitions.assistant.labels.closed_desc') }}</p>
                <p class="type-hint" v-else>{{ $t('competitions.assistant.labels.select_hint') }}</p>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowFees" class="field">
            <div class="card-sub">
              <strong>{{ $t('competitions.assistant.labels.fees_title') }}</strong>
            </div>
            <div class="field-grid">
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.fees') }}</label>
                <div class="input-prefix">
                  <span class="prefix">{{ currencySymbol() }}</span>
                  <input
                      type="number"
                      min="0"
                      step="0.01"
                      v-model.number="form.fees"
                      @input="prizeError = ''"
                      :placeholder="$t('competitions.assistant.placeholders.fees')"
                  />
                </div>
                <p class="helper-text">{{ $t('competitions.assistant.labels.fees_helper') }}</p>
              </div>
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.prize_total') }}</label>
                <div class="input-prefix">
                  <span class="prefix">{{ currencySymbol() }}</span>
                  <input
                      type="number"
                      min="0"
                      step="1"
                      v-model.number="form.prize_amount_total"
                      @input="onPrizeTotalChange"
                      :placeholder="$t('competitions.assistant.placeholders.prize')"
                  />
                </div>
                <p class="helper-text">
                  {{ $t('competitions.assistant.labels.prize_total_helper') }}
                  <span v-if="isPrizePercentageMode" class="percentage-badge">{{ $t('competitions.assistant.labels.prize_zero_badge') }}</span>
                </p>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowPrizeDetails" class="field">
            <div class="field">
              <label>{{ $t('competitions.assistant.labels.prize_split') }}</label>
              <div class="type-grid">
                <div
                    class="type-card"
                    :class="{ active: form.prize_split_count === 3 }"
                    @click="onPrizeSplitChange(3)"
                >
                  <div class="type-header">
                    <span class="pill">{{ $t('competitions.assistant.labels.prize_top3') }}</span>
                    <i v-if="form.prize_split_count === 3" class="fa-solid fa-check"></i>
                  </div>
                  <p class="type-hint" v-if="form.prize_split_count !== 3">{{ $t('competitions.assistant.labels.select_hint') }}</p>
                  <p class="type-description" v-else>{{ $t('competitions.assistant.labels.prize_top3_desc') }}</p>
                </div>
                <div
                    class="type-card"
                    :class="{ active: form.prize_split_count === 4 }"
                    @click="onPrizeSplitChange(4)"
                >
                  <div class="type-header">
                    <span class="pill">{{ $t('competitions.assistant.labels.prize_top4') }}</span>
                    <i v-if="form.prize_split_count === 4" class="fa-solid fa-check"></i>
                  </div>
                  <p class="type-hint" v-if="form.prize_split_count !== 4">{{ $t('competitions.assistant.labels.select_hint') }}</p>
                  <p class="type-description" v-else>{{ $t('competitions.assistant.labels.prize_top4_desc') }}</p>
                </div>
              </div>
            </div>

            <div class="field-grid">
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.prize_first') }}</label>
                <div class="input-prefix">
                  <span class="prefix">{{ currencySymbol() }}</span>
                  <input type="number" min="0" step="1" v-model.number="form.prize_amount_first" @input="onPrizeManualChange" />
                </div>
              </div>
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.prize_second') }}</label>
                <div class="input-prefix">
                  <span class="prefix">{{ currencySymbol() }}</span>
                  <input type="number" min="0" step="1" v-model.number="form.prize_amount_second" @input="onPrizeManualChange" />
                </div>
              </div>
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.prize_third') }}</label>
                <div class="input-prefix">
                  <span class="prefix">{{ currencySymbol() }}</span>
                  <input type="number" min="0" step="1" v-model.number="form.prize_amount_third" @input="onPrizeManualChange" />
                </div>
              </div>
              <div class="field" v-if="form.prize_split_count === 4">
                <label>{{ $t('competitions.assistant.labels.prize_fourth') }}</label>
                <div class="input-prefix">
                  <span class="prefix">{{ currencySymbol() }}</span>
                  <input type="number" min="0" step="1" v-model.number="form.prize_amount_fourth" @input="onPrizeManualChange" />
                </div>
              </div>
            </div>
            <p v-if="prizeError" class="error-text">{{ prizeError }}</p>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="canShowPrizeImage" class="field">
            <label>{{ $t('competitions.assistant.labels.image') }}</label>
            <p class="helper-text">{{ $t('competitions.assistant.labels.image_helper') }}</p>
            <div class="image-upload">
              <div class="cropper-wrapper" v-if="showCropper">
                <VueCropper
                    ref="cropper"
                    :guides="false"
                    :src="image"
                    :autoCropArea="1"
                    :aspectRatio="16 / 9"
                    :viewMode="1"
                    :dragMode="dragMode"
                />
                <div class="image-actions">
                  <button class="upload-btn" type="button" @click.prevent="saveCroppedImage">
                    <i class="fa-solid fa-crop"></i>
                    <span>{{ $t('competitions.assistant.labels.image_save_crop') }}</span>
                  </button>
                  <button class="btn-clear" type="button" @click.prevent="cancelCropping">{{ $t('competitions.assistant.labels.image_cancel_crop') }}</button>
                </div>
              </div>

              <div v-else class="preview-stack">
                <img :src="imagePreview || form.img_path || defaultImage" alt="Imagen de la competición" class="image-preview" />
                <div class="image-actions">
                  <label class="upload-btn">
                    <i class="fa-solid fa-upload"></i>
                    <span>{{ $t('competitions.assistant.labels.image_upload') }}</span>
                    <input type="file" accept="image/*" @change="handleImageUpload" />
                  </label>
                  <button v-if="form.img_path || imagePreview" type="button" class="btn-clear" @click="clearImage">{{ $t('competitions.assistant.labels.image_remove') }}</button>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade-step">
          <div v-if="form.img_path" class="field">
            <div class="field-grid">
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.links_event') }}</label>
                <input
                    type="url"
                    v-model="form.url_event"
                    @input="onUrlInput('url_event')"
                    :placeholder="$t('competitions.assistant.placeholders.url_event')"
                />
                <p v-if="urlErrors.url_event" class="error-text">{{ urlErrors.url_event }}</p>
              </div>
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.links_social') }}</label>
                <input
                    type="url"
                    v-model="form.url_social"
                    @input="onUrlInput('url_social')"
                    :placeholder="$t('competitions.assistant.placeholders.url_social')"
                />
                <p v-if="urlErrors.url_social" class="error-text">{{ urlErrors.url_social }}</p>
              </div>
              <div class="field">
                <label>{{ $t('competitions.assistant.labels.links_location') }}</label>
                <input
                    type="url"
                    v-model="form.location"
                    @input="onUrlInput('location')"
                    :placeholder="$t('competitions.assistant.placeholders.location')"
                />
                <p class="helper-text">{{ $t('competitions.assistant.labels.links_location_helper') }}</p>
                <p v-if="urlErrors.location" class="error-text">{{ urlErrors.location }}</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </section>

    <div class="actions">
      <button class="btn-secondary" @click="goBack">{{ $t('competitions.assistant.buttons.back') }}</button>
      <div class="actions-right">
        <button class="btn-secondary btn-small" @click="openClearConfirm" :disabled="saving || loading">{{ $t('competitions.assistant.buttons.clear') }}</button>
        <button
            class="btn-validate"
            @click="openValidationConfirm"
            :disabled="!canRequestValidation"
        >
          {{ $t('competitions.assistant.buttons.request_validation') }}
        </button>
        <button class="btn-primary" @click="saveDraft" :disabled="saving || loading || !canSaveDraft">
          <span v-if="saving" class="spinner"></span>
          {{ $t('competitions.assistant.buttons.save_draft') }}
        </button>
      </div>
    </div>
  </div>
  <loading-modal ref="loadingModal"></loading-modal>
  <ConfirmationModal
      ref="clearConfirmModal"
        :message="clearConfirmMessage"
      :showCancel="true"
      @confirmed="confirmClear"
  />
  <ConfirmationModal
      ref="validateConfirmModal"
        :message="validateConfirmMessage"
      :showCancel="true"
      @confirmed="confirmValidation"
  />
</template>

<script>
import { mapState } from "vuex";
import LoadingModal from "@/services/LoadingModal.vue";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
import { secureStorage } from "@/services/secureStorage";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";
import defaultImage from "@/assets/competition.jpg";

const COUNTRY_ALLOWLIST = [
  'ES', 'IT', 'FR', 'DE', 'CZ', 'CH', 'PT', 'US', 'CA', 'MX', 'CO', 'CL', 'BR', 'AR', 'PE', 'JP', 'PL'
];
const MOBILE_BREAKPOINT = 768;

export default {
  name: "CompetitionWizardAssistant",
  components: { LoadingModal, VueDatePicker, VueCropper, ConfirmationModal },
  data() {
    return {
      form: {
        name: "",
        country_id: null,
        date_start: "",
        date_start_time: "",
        date_end: "",
        date_end_time: "",
        type_id: null,
        flow_scope: null,
        is_qualification_dual: null,
        is_vs: null,
        round_size_id: [],
        is_open: null,
        gender: null,
        category: null,
        rule_id: null,
        tricklist_version_id: null,
        access_link: "",
        location: "",
        url_event: "",
        url_social: "",
        fees: "",
        prize_amount_total: "",
        prize_amount_first: "",
        prize_amount_second: "",
        prize_amount_third: "",
        prize_amount_fourth: "",
        prize_split_count: 3,
        img_path: ""
      },
      startDateTime: null,
      endDateTime: null,
      countries: [],
      rules: [],
      tricklistVersions: [],
      genders: [
        { value: "male", name: "Males only" },
        { value: "female", name: "Females only" },
        { value: "mixed", name: "Mixed gender" }
      ],
      categories: [
        { value: "national", name: "National" },
        { value: "international", name: "International" },
        { value: "team", name: "Team" }
      ],
      showCropper: false,
      image: null,
      dragMode: "move",
      finalImagePath: null,
      defaultImage,
      hasUnsavedChanges: false,
      isInitializing: true,
      hideMobileHeader: false,
      lastScrollY: 0,
      urlErrors: {
        url_event: "",
        url_social: "",
        location: "",
      }
    };
  },
  computed: {
    ...mapState("user", ["token"]),
    ...mapState("competition", ["competitionIdToEdit"]),
    requiredKeys() {
      const base = [
        "name",
        "country_id",
        "date_start",
        "date_start_time",
        "type_id",
        "flow_scope",
        "is_qualification_dual",
        "category",
        "is_open",
        "rule_id",
        "tricklist_version_id"
      ];
      if (this.isFullFlow) {
        base.push("is_vs");
      }
      return base;
    },
    isOnline() {
      return Number(this.form.type_id) === 1;
    },
    isInPersonOrMixed() {
      const type = Number(this.form.type_id);
      return type === 2;
    },
    isFullFlow() {
      return this.form.flow_scope === "full";
    },
    typeOptions() {
      return [
        {
          id: 1,
          label: this.$t('competitions.assistant.labels.type_options.online_label'),
          description: this.$t('competitions.assistant.labels.type_options.online_desc'),
          disabled: false
        },
        {
          id: 2,
          label: this.$t('competitions.assistant.labels.type_options.inperson_label'),
          description: this.$t('competitions.assistant.labels.type_options.inperson_desc'),
          disabled: false
        }
      ];
    },
    canSaveDraft() {
      return this.isFilled(this.form.name);
    },
    canShowCountry() {
      return this.isFilled(this.form.name);
    },
    canShowDates() {
      return this.canShowType && this.isFilled(this.form.type_id);
    },
    canEnableEnd() {
      return this.isFilled(this.form.date_start) && this.isFilled(this.form.date_start_time);
    },
    canShowType() {
      return this.canShowCountry && this.isFilled(this.form.country_id);
    },
    canShowFlowScope() {
      return (
        this.isFilled(this.form.type_id) &&
        this.isFilled(this.form.date_start) &&
        this.isFilled(this.form.date_start_time)
      );
    },
    canShowQualification() {
      return this.canShowFlowScope && this.isFilled(this.form.flow_scope);
    },
    canShowMode() {
      return this.canShowType && this.isFullFlow && this.isInPersonOrMixed && this.isFilled(this.form.is_qualification_dual);
    },
    canShowRule() {
      if (!(this.canShowType && this.isFilled(this.form.type_id))) return false;
      if (!this.isFilled(this.form.is_qualification_dual)) return false;
      if (!this.isFullFlow) {
        // Solo calificaciones: avanzamos directamente a filtros
        return true;
      }
      if (this.isInPersonOrMixed) {
        // Flujo completo presencial/mixto: basta con elegir modalidad para continuar
        return this.isFilled(this.form.is_vs);
      }
      // Flujo completo online: se exige modalidad (fijada automáticamente en selectType)
      return this.isFilled(this.form.is_vs);
    },
    canShowOpen() {
      return (
        this.canShowRule &&
        this.isFilled(this.form.gender) &&
        this.isFilled(this.form.category) &&
        this.isFilled(this.form.rule_id)
      );
    },
    canShowFees() {
      return this.isFilled(this.form.is_open);
    },
    canShowPrizeDetails() {
      return this.canShowFees && Number(this.form.prize_amount_total) > 0;
    },
    canShowPrizeImage() {
      const rawTotal = this.form.prize_amount_total;
      const isZeroTotal = rawTotal === 0;
      const hasTotal = this.isFilled(rawTotal) || isZeroTotal;
      return this.canShowFees && hasTotal;
    },
    isPrizePercentageMode() {
      const rawTotal = this.form.prize_amount_total;
      return this.canShowFees && rawTotal === 0;
    },
    canRequestValidation() {
      return (
        !this.saving &&
        !this.loading &&
        this.progress === 100 &&
        !!this.competitionIdToEdit &&
        !this.hasUnsavedChanges
      );
    },
    progress() {
      const baseFilled = this.requiredKeys.filter((key) => this.isFilled(this.form[key])).length;

      // Enlaces de interés: contar el bloque solo cuando los tres estén completos
      const linksComplete = this.isFilled(this.form.url_event) && this.isFilled(this.form.url_social) && this.isFilled(this.form.location);

      // Premios: total + individuales (incluye 4.º si aplica)
      const rawPrizeTotal = this.form.prize_amount_total;
      const prizeTotalFilled = this.isFilled(rawPrizeTotal) || this.isExplicitZero(rawPrizeTotal);
      const prizeTotalNumber = prizeTotalFilled ? Number(rawPrizeTotal) : NaN;
      const isPrizeTotalZero = prizeTotalFilled && !Number.isNaN(prizeTotalNumber) && prizeTotalNumber === 0;
      const prizeIndividualsFilled =
        this.isFilled(this.form.prize_amount_first) &&
        this.isFilled(this.form.prize_amount_second) &&
        this.isFilled(this.form.prize_amount_third) &&
        (this.form.prize_split_count !== 4 || this.isFilled(this.form.prize_amount_fourth));
      const prizesComplete = prizeTotalFilled && (isPrizeTotalZero ? true : prizeIndividualsFilled);

      const feesComplete = this.isFilled(this.form.fees) || this.isExplicitZero(this.form.fees);
      const financeComplete = feesComplete && prizesComplete;

      const extraKeysCount = 3; // bloque inscripción+premios, img_path, enlaces bloque
      const extraFilled =
        (financeComplete ? 1 : 0) +
        (this.isFilled(this.form.img_path) ? 1 : 0) +
        (linksComplete ? 1 : 0);

      const total = this.requiredKeys.length + extraKeysCount;
      const filled = baseFilled + extraFilled;

      return Math.round((filled / total) * 100);
    },
    checklist() {
      const list = [
        {
          key: "basic",
          label: this.$t('competitions.assistant.checklist.basic'),
          done:
            this.isFilled(this.form.name) &&
            this.isFilled(this.form.country_id) &&
            this.isFilled(this.form.date_start) &&
            this.isFilled(this.form.date_start_time)
        },
        { key: "type_id", label: this.$t('competitions.assistant.checklist.type'), done: this.isFilled(this.form.type_id) },
        { key: "flow_scope", label: this.$t('competitions.assistant.checklist.flow_scope'), done: this.isFilled(this.form.flow_scope) },
        { key: "is_qualification_dual", label: this.$t('competitions.assistant.checklist.qualification'), done: this.isFilled(this.form.is_qualification_dual) },
      ];

      if (this.isFullFlow) {
        list.push({ key: "is_vs", label: this.$t('competitions.assistant.checklist.mode'), done: this.isFilled(this.form.is_vs) });
      }

      list.push({
        key: "filters",
        label: this.$t('competitions.assistant.checklist.filters'),
        done:
          this.isFilled(this.form.gender) &&
          this.isFilled(this.form.category) &&
          this.isFilled(this.form.rule_id)
      });

      list.push({ key: "is_open", label: this.$t('competitions.assistant.checklist.access'), done: this.isFilled(this.form.is_open) });

      list.push({
        key: "finance",
        label: this.$t('competitions.assistant.checklist.finance'),
        done: (() => {
          const feesComplete = this.isFilled(this.form.fees) || this.isExplicitZero(this.form.fees);

          const rawPrizeTotal = this.form.prize_amount_total;
          const prizeTotalFilled = this.isFilled(rawPrizeTotal) || this.isExplicitZero(rawPrizeTotal);
          const prizeTotalNumber = prizeTotalFilled ? Number(rawPrizeTotal) : NaN;
          const isPrizeTotalZero = prizeTotalFilled && !Number.isNaN(prizeTotalNumber) && prizeTotalNumber === 0;

          const prizeIndividualsFilled =
            this.isFilled(this.form.prize_amount_first) &&
            this.isFilled(this.form.prize_amount_second) &&
            this.isFilled(this.form.prize_amount_third) &&
            (this.form.prize_split_count !== 4 || this.isFilled(this.form.prize_amount_fourth));

          const prizesComplete = prizeTotalFilled && (isPrizeTotalZero ? true : prizeIndividualsFilled);

          return feesComplete && prizesComplete;
        })()
      });

      list.push({ key: "img_path", label: this.$t('competitions.assistant.checklist.image'), done: this.isFilled(this.form.img_path) });
      list.push({ key: "links", label: this.$t('competitions.assistant.checklist.links'), done: this.isFilled(this.form.url_event) && this.isFilled(this.form.url_social) && this.isFilled(this.form.location) });

      return list;
    },
    minStartDate() {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 1);
      return d;
    }
  },
  mounted() {
    if (this.competitionIdToEdit) {
      this.loadDraft();
      this.fetchCompetition(this.competitionIdToEdit);
    } else {
      this.clearDraftStorage();
      this.isInitializing = false;
    }
    this.fetchCountries();
    this.fetchRules();
    this.lastScrollY = window.scrollY || 0;
    window.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    showLoading() {
      this.loading = true;
      this.$nextTick(() => {
        this.$refs.loadingModal?.showLoading();
      });
    },
    hideLoading() {
      this.loading = false;
      this.$nextTick(() => {
        this.$refs.loadingModal?.hideLoading();
      });
    },
    onScroll() {
      const current = window.scrollY || 0;
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      if (!isMobile) {
        if (this.hideMobileHeader) {
          this.hideMobileHeader = false;
        }
        this.lastScrollY = current;
        return;
      }

      const delta = current - this.lastScrollY;
      const threshold = 12;

      if (current <= 20) {
        this.hideMobileHeader = false;
      } else if (delta > threshold) {
        this.hideMobileHeader = true;
      } else if (delta < -threshold) {
        this.hideMobileHeader = false;
      }

      this.lastScrollY = current;
    },
    openClearConfirm() {
      this.$refs.clearConfirmModal?.openModal();
    },
    confirmClear() {
      this.clearDraft();
    },
    openValidationConfirm() {
      this.$refs.validateConfirmModal?.openModal();
    },
    async confirmValidation() {
      if (!this.competitionIdToEdit) {
        return;
      }
      this.showLoading();
      try {
        const response = await this.$axios.post(
          "/status/update",
          {
            entity: "competitions",
            id: this.competitionIdToEdit,
            new_status: 3,
            source: "admin"
          },
          {
            headers: { Authorization: `Bearer ${this.token}` }
          }
        );
        if (response?.status === 200) {
          this.$router.push("/competitions");
        } else {
          this.formError = this.$t("competitions.assistant.messages.validation_error");
        }
      } catch (error) {
        console.error("Error al solicitar validación", error);
        this.formError = this.$t("competitions.assistant.messages.validation_error");
      } finally {
        this.hideLoading();
      }
    },
    toNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    },
    isExplicitZero(value) {
      if (value === 0) return true;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return false;
        const n = Number(trimmed);
        return Number.isFinite(n) && n === 0;
      }
      const n = Number(value);
      return Number.isFinite(n) && n === 0;
    },
    normalizeMoneyDisplay(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      if (Number.isInteger(num)) return num;
      return Number(num.toFixed(2));
    },
    currencySymbol() {
      const found = this.countries.find((c) => c.id === this.form.country_id);
      return found?.currency_symbol || "€";
    },
    formatDateTime(date, time) {
      if (!date) return null;
      const hhmm = (time || "00:00").padEnd(5, "0");
      return `${date} ${hhmm}:00`;
    },
    isFilled(value) {
      return value !== null && value !== undefined && String(value).trim() !== "";
    },
    selectType(id) {
      const option = this.typeOptions.find((opt) => opt.id === id);
      if (option?.disabled) return;

      this.form.type_id = id;
      if (this.isOnline) {
        this.form.is_vs = 0;
        // Para online, sincronizamos la fecha de fin con inicio
        if (this.startDateTime) {
          const start = new Date(this.startDateTime);
          if (!Number.isNaN(start.getTime())) {
            this.endDateTime = start;
            this.form.date_end = this.dateToYMD(start);
            this.form.date_end_time = this.dateToHM(start);
          }
        }
      } else {
        // Resetear modo y etapas al cambiar a presencial
        this.form.is_vs = null;
        this.form.round_size_id = [];
      }
      this.persist();
    },
    selectFlowScope(value) {
      this.form.flow_scope = value;
      if (value !== "full") {
        this.form.is_vs = 0;
        this.form.is_qualification_dual = null;
        this.form.round_size_id = [];
      } else {
        // En flujo completo, online fuerza modo scoring; presencial/mixto se reabre la elección
        this.form.is_qualification_dual = null;
        this.form.is_vs = this.isOnline ? 0 : null;
        this.form.round_size_id = [];
      }
      this.persist();
    },
    selectQualification(value) {
      this.form.is_qualification_dual = value;
      this.persist();
    },
    selectMode(value) {
      this.form.is_vs = value;
      this.persist();
    },
    onPrizeSplitChange(count) {
      this.form.prize_split_count = count;
      if (count === 3) {
        this.form.prize_amount_fourth = "";
      }
      if (!this.prizeManualEdits) {
        this.autoDistributePrizes();
      }
      this.prizeError = "";
    },
    onPrizeTotalChange() {
      const raw = this.form.prize_amount_total;
      // Si el campo está vacío, no forzar a 0 y limpiar montos individuales
      if (raw === null || raw === undefined || String(raw).trim() === "") {
        this.form.prize_amount_total = "";
        this.prizeManualEdits = false;
        this.prizeError = "";
        this.clearPrizeSplits();
        return;
      }

      this.normalizePrizeIntegerFields(["prize_amount_total"]);
      this.prizeManualEdits = false;
      this.prizeError = "";
      this.autoDistributePrizes();
    },
    onPrizeManualChange() {
      this.normalizePrizeIntegerFields([
        "prize_amount_first",
        "prize_amount_second",
        "prize_amount_third",
        "prize_amount_fourth"
      ]);
      this.prizeManualEdits = true;
      this.prizeError = "";
    },
    normalizePrizeIntegerFields(fields) {
      fields.forEach((field) => {
        const raw = this.form[field];
        if (raw === "" || raw === null || raw === undefined) return;
        const n = Math.max(0, Math.round(Number(raw) || 0));
        this.form[field] = n;
      });
    },
    autoDistributePrizes() {
      this.normalizePrizeIntegerFields(["prize_amount_total"]);
      const rawTotal = this.form.prize_amount_total;

      if (rawTotal === null || rawTotal === undefined || String(rawTotal).trim() === "") {
        this.clearPrizeSplits();
        return;
      }

      const total = Number(rawTotal);
      if (!Number.isFinite(total)) {
        this.clearPrizeSplits();
        return;
      }

      if (total <= 0) {
        this.clearPrizeSplits();
        // Mantener 0 explícito si el usuario lo ingresó
        this.form.prize_amount_total = total;
        return;
      }
      const split = this.form.prize_split_count === 4 ? 4 : 3;
      const distributions = {
        3: [0.5, 0.3, 0.2],
        4: [0.4, 0.3, 0.2, 0.1]
      };
      const ratios = distributions[split];
      const base = ratios.map((r) => Math.floor(total * r));
      let remainder = total - base.reduce((a, b) => a + b, 0);
      let idx = 0;
      while (remainder > 0) {
        base[idx % base.length] += 1;
        remainder -= 1;
        idx += 1;
      }

      [this.form.prize_amount_first, this.form.prize_amount_second, this.form.prize_amount_third] = base;
      this.form.prize_amount_fourth = split === 4 ? base[3] : "";
    },

    clearPrizeSplits() {
      this.form.prize_amount_first = "";
      this.form.prize_amount_second = "";
      this.form.prize_amount_third = "";
      this.form.prize_amount_fourth = this.form.prize_split_count === 4 ? "" : this.form.prize_amount_fourth;
    },
    prizeSum() {
      const nums = [
        Math.round(Number(this.form.prize_amount_first) || 0),
        Math.round(Number(this.form.prize_amount_second) || 0),
        Math.round(Number(this.form.prize_amount_third) || 0)
      ];
      if (this.form.prize_split_count === 4) {
        nums.push(Math.round(Number(this.form.prize_amount_fourth) || 0));
      }
      return nums.reduce((a, b) => a + b, 0);
    },
    validatePrizes() {
      this.prizeError = "";
      const hasAnyPrize =
        this.form.prize_amount_total ||
        this.form.prize_amount_first ||
        this.form.prize_amount_second ||
        this.form.prize_amount_third ||
        this.form.prize_amount_fourth;
      if (!hasAnyPrize) {
        return true;
      }
      this.normalizePrizeIntegerFields([
        "prize_amount_total",
        "prize_amount_first",
        "prize_amount_second",
        "prize_amount_third",
        "prize_amount_fourth"
      ]);
      const total = Math.round(Number(this.form.prize_amount_total) || 0);
      const sum = this.prizeSum();
      if (sum > total) {
        this.prizeError = this.$t("competitions.prize_error_total");
        return false;
      }
      if (total > 0 && sum !== total) {
        this.prizeError = this.$t("competitions.prize_error_total");
        return false;
      }
      if (total === 0 && sum > 0) {
        this.prizeError = this.$t("competitions.prize_error_total_required");
        return false;
      }
      return true;
    },
    handleImageUpload(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.image = e.target.result;
        this.showCropper = true;
      };
      reader.readAsDataURL(file);
    },
    cancelCropping() {
      this.showCropper = false;
      this.image = null;
    },
    async saveCroppedImage() {
      this.$nextTick(async () => {
        const cropperInstance = Array.isArray(this.$refs.cropper) ? this.$refs.cropper[0] : this.$refs.cropper;
        if (cropperInstance && cropperInstance.getCroppedCanvas) {
          const canvas = cropperInstance.getCroppedCanvas({
            width: 1280,
            height: 720,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: "high"
          });
          canvas.toBlob(async (blob) => {
            if (!blob) {
              return;
            }
            const file = new File([blob], "image.jpg", { type: blob.type });
            this.croppedImageBlob = file;
            this.croppedImage = URL.createObjectURL(file);
            this.showCropper = false;
            await this.uploadImageToServer();
          }, "image/jpeg", 0.7);
        }
      });
    },
    async uploadImageToServer() {
      if (!this.croppedImageBlob) return;
      const formData = new FormData();
      formData.append("image", this.croppedImageBlob, "image.jpg");
      formData.append("store", "competitions_banners");
      this.showLoading();
      try {
        const response = await this.$axios.post(`/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "multipart/competitions-data"
          }
        });
        if (response.status === 200 && response.data?.path) {
          this.finalImagePath = response.data.path;
          this.form.img_path = this.finalImagePath;
          this.imagePreview = this.croppedImage;
          this.persist();
        }
      } catch (error) {
        console.error("Error al subir la imagen", error);
      } finally {
        this.hideLoading();
      }
    },
    clearImage() {
      this.form.img_path = "";
      this.imagePreview = "";
      this.croppedImage = null;
      this.croppedImageBlob = null;
      this.finalImagePath = null;
      this.image = null;
      this.showCropper = false;
      this.persist();
    },
    selectOpen(value) {
      this.form.is_open = value;
      this.persist();
    },
    onUrlInput(key) {
      this.persist();
      this.clearUrlError(key);
    },
    clearUrlError(key) {
      if (!this.urlErrors[key]) return;
      const limit = 255;
      const value = this.form[key];
      if (!value || String(value).length <= limit) {
        this.urlErrors = { ...this.urlErrors, [key]: "" };
        if (!Object.values(this.urlErrors).some(Boolean)) {
          this.formError = "";
        }
      }
    },
    onStartChange(value) {
      this.startDateTime = value;
      if (value) {
        const start = new Date(value);
        if (Number.isNaN(start.getTime())) {
          this.form.date_start = "";
          this.form.date_start_time = "";
        } else {
          this.form.date_start = this.dateToYMD(start);
          this.form.date_start_time = this.dateToHM(start);
          if (this.isOnline) {
            // En online, fin = inicio
            this.endDateTime = start;
            this.form.date_end = this.dateToYMD(start);
            this.form.date_end_time = this.dateToHM(start);
          } else {
            const endAuto = new Date(start.getTime() + 8 * 60 * 60 * 1000);
            this.endDateTime = endAuto;
            this.form.date_end = this.dateToYMD(endAuto);
            this.form.date_end_time = this.dateToHM(endAuto);
          }
        }
      } else {
        this.form.date_start = "";
        this.form.date_start_time = "";
        this.form.date_end = "";
        this.form.date_end_time = "";
        this.endDateTime = null;
      }
      this.persist();
    },
    onEndChange(value) {
      this.endDateTime = value;
      if (value) {
        this.form.date_end = this.dateToYMD(value);
        this.form.date_end_time = this.dateToHM(value);
      } else {
        this.form.date_end = "";
        this.form.date_end_time = "";
      }
      this.persist();
    },
    dateToYMD(date) {
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const da = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${da}`;
    },
    dateToHM(date) {
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "";
      const h = String(d.getHours()).padStart(2, "0");
      const m = String(d.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    },
    ensureModeForType() {
      if (this.isOnline) {
        this.form.is_vs = 0;
      }
    },
    persist() {
      const startTimestamp = this.formatDateTime(this.form.date_start, this.form.date_start_time);
      const endDateForPersist = this.form.date_end || this.form.date_start || null;
      const endTimeForPersist = this.form.date_end_time || this.form.date_start_time;
      const endTimestamp = endDateForPersist ? this.formatDateTime(endDateForPersist, endTimeForPersist) : null;
      const draft = {
        ...this.form,
        date_start: startTimestamp,
        date_end: endTimestamp,
        _context_competition_id: this.competitionIdToEdit || null
      };
      secureStorage.setItem("competitionAssistantData", draft);
      if (!this.isInitializing) {
        this.hasUnsavedChanges = true;
      }
    },
    loadDraft() {
      const saved = secureStorage.getItem("competitionAssistantData");
      if (saved) {
        const draftContext = saved._context_competition_id ?? null;
        const currentContext = this.competitionIdToEdit || null;
        if (draftContext !== currentContext) {
          secureStorage.removeItem("competitionAssistantData");
          return;
        }
        this.form = { ...this.form, ...saved };
        this.form.country_id = this.toNumber(this.form.country_id);
        if (!Array.isArray(this.form.round_size_id)) {
          this.form.round_size_id = [];
        }
        this.imagePreview = this.form.img_path || "";
        if (saved.date_start) {
          const [d, t] = saved.date_start.split(" ");
          this.form.date_start = d || "";
          this.form.date_start_time = (t || "").slice(0,5);
          this.startDateTime = new Date(saved.date_start.replace(" ", "T"));
        }
        if (saved.date_end) {
          const [d, t] = saved.date_end.split(" ");
          this.form.date_end = d || "";
          this.form.date_end_time = (t || "").slice(0,5);
          this.endDateTime = new Date(saved.date_end.replace(" ", "T"));
        } else if (saved.date_start) {
          this.form.date_end = this.form.date_start;
          this.form.date_end_time = this.form.date_start_time;
          this.endDateTime = this.startDateTime;
        }
        this.form.fees = this.normalizeMoneyDisplay(this.form.fees);
        this.form.prize_amount_total = this.normalizeMoneyDisplay(this.form.prize_amount_total);
        this.ensureModeForType();
      }
    },
    async fetchCompetition(id) {
      try {
        this.showLoading();
        const response = await this.$axios.get(`/competition/${id}`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        const competition = response.data?.competition;
        if (!competition) return;

        this.form.name = competition.name || "";
        const competitionCountryId = competition.competitions_info?.country_id ?? competition.country_id ?? null;
        this.form.country_id = this.toNumber(competitionCountryId);
        this.form.type_id = competition.type_id || null;
        const flowScope = competition.competitions_info?.flow_scope ?? competition.flow_scope ?? null;
        this.form.flow_scope = flowScope;
        this.form.is_qualification_dual = competition.is_qualification_dual ?? null;
        this.form.rule_id = competition.rule_id || null;
        this.form.tricklist_version_id = competition.tricklist_version_id || competition.tricklist_version?.id || null;
        this.form.is_vs = competition.is_vs ?? null;
        this.form.img_path = competition.competitions_info?.img_path || "";
        this.imagePreview = this.form.img_path;
        this.form.location = competition.competitions_info?.location || "";
        this.form.url_event = competition.competitions_info?.url_event || "";
        this.form.url_social = competition.competitions_info?.url_social || "";
        this.form.fees = this.normalizeMoneyDisplay(
          competition.competitions_info?.fees ?? competition.fees ?? ""
        );
        this.form.prize_amount_total = this.normalizeMoneyDisplay(
          competition.competitions_info?.prize_amount_total ?? competition.competitions_info?.prize_amount ?? ""
        );
        this.form.prize_amount_first = competition.competitions_info?.prize_amount_first ?? "";
        this.form.prize_amount_second = competition.competitions_info?.prize_amount_second ?? "";
        this.form.prize_amount_third = competition.competitions_info?.prize_amount_third ?? "";
        this.form.prize_amount_fourth = competition.competitions_info?.prize_amount_fourth ?? "";
        this.form.prize_split_count = competition.competitions_info?.prize_split_count ?? this.form.prize_split_count;
        const roundSizesFromRelation = Array.isArray(competition.round_sizes)
          ? competition.round_sizes
              .map((rs) => rs?.id || rs?.round_size_id || rs?.pivot?.round_size_id)
              .filter((v) => Number.isFinite(Number(v)))
              .map((v) => Number(v))
          : [];
        const roundSizesFlat = Array.isArray(competition.round_size_id) ? competition.round_size_id : [];
        this.form.round_size_id = [...new Set([...roundSizesFlat, ...roundSizesFromRelation])];
        this.form.gender = competition.competitions_info?.gender || null;
        this.form.category = competition.competitions_info?.category || null;
        this.form.is_open = competition.is_open ?? null;

        const start = competition.competitions_info?.date_start || "";
        const end = competition.competitions_info?.date_end || "";
        if (start) {
          const [d, t] = start.split(" ");
          this.form.date_start = d || "";
          this.form.date_start_time = (t || "").slice(0,5);
          this.startDateTime = new Date(start.replace(" ", "T"));
        }
        if (end) {
          const [d, t] = end.split(" ");
          this.form.date_end = d || "";
          this.form.date_end_time = (t || "").slice(0,5);
          this.endDateTime = new Date(end.replace(" ", "T"));
        } else if (start) {
          this.form.date_end = this.form.date_start;
          this.form.date_end_time = this.form.date_start_time;
          this.endDateTime = this.startDateTime;
        }

        this.ensureModeForType();

        this.persist();
      } catch (error) {
        console.error("Error al cargar competición para edición", error);
      } finally {
        this.hideLoading();
        this.hasUnsavedChanges = false;
        this.isInitializing = false;
      }
    },
    clearDraft() {
      this.form = {
        name: "",
        country_id: null,
        date_start: "",
        date_start_time: "",
        date_end: "",
        date_end_time: "",
        type_id: null,
        is_qualification_dual: null,
        is_vs: null,
        round_size_id: [],
        is_open: null,
        gender: null,
        category: null,
        rule_id: null,
        tricklist_version_id: null,
        access_link: "",
        flow_scope: null,
        fees: "",
        prize_amount_total: "",
        prize_amount_first: "",
        prize_amount_second: "",
        prize_amount_third: "",
        prize_amount_fourth: "",
        prize_split_count: 3,
        img_path: "",
        location: "",
        url_event: "",
        url_social: ""
      };
      this.startDateTime = null;
      this.endDateTime = null;
      this.prizeError = "";
      this.formError = "";
      this.imagePreview = "";
      this.showCropper = false;
      this.croppedImage = null;
      this.croppedImageBlob = null;
      this.image = null;
      this.finalImagePath = null;
      this.clearDraftStorage();
      this.hasUnsavedChanges = false;
    },
    clearDraftStorage() {
      secureStorage.removeItem("competitionAssistantData");
    },
    async fetchCountries() {
      try {
        this.showLoading();
        const response = await this.$axios.get("/countries", {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        const raw = Array.isArray(response.data?.countries) ? response.data.countries : [];
        const filtered = raw.filter((c) => COUNTRY_ALLOWLIST.includes((c.iso2 || "").toUpperCase()));
        const mapped = filtered.map((country) => {
          let translatedName = country.name;
          let langCode = this.$i18n?.locale || "en";
          if (langCode === "jp") langCode = "ja";
          if (langCode !== "en" && country.translations) {
            try {
              const translationsObject = JSON.parse(country.translations);
              translatedName = translationsObject[langCode] || country.name;
            } catch (error) {
              translatedName = country.name;
            }
          }
          return {
            id: this.toNumber(country.id),
            name: translatedName,
            iso2: country.iso2,
            currency_symbol: country.currency_symbol
          };
        });
        this.countries = mapped.sort((a, b) => a.name.localeCompare(b.name));
        // If the currently set country_id is not in the allowlist, try to keep it as a fallback option
        if (this.form.country_id && !this.countries.some((c) => c.id === this.form.country_id)) {
          const fallback = raw.find((c) => this.toNumber(c.id) === this.form.country_id);
          if (fallback) {
            this.countries.push({
                id: this.toNumber(fallback.id),
                name: fallback.name,
                iso2: fallback.iso2,
                currency_symbol: fallback.currency_symbol
            });
          }
        }
      } catch (error) {
        console.error("Error al cargar países", error);
      } finally {
        this.hideLoading();
      }
    },
    async fetchRules() {
      try {
        this.showLoading();
        const response = await this.$axios.get("/competitions", {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        const rulesRaw = Array.isArray(response.data?.rules) ? response.data.rules : [];
        this.rules = rulesRaw.map((r) => ({ value: r.id, name: r.name }));
        const tricklistVersionsRaw = Array.isArray(response.data?.tricklist_versions) ? response.data.tricklist_versions : [];
        this.tricklistVersions = tricklistVersionsRaw.map((version) => ({
          value: version.id,
          name: version.name || `${version.year}`,
        }));

        if (!this.form.tricklist_version_id && this.tricklistVersions.length) {
          this.form.tricklist_version_id = this.tricklistVersions[0].value;
        }
      } catch (error) {
        console.error("Error al cargar reglas", error);
      } finally {
        this.hideLoading();
      }
    },
    async saveDraft() {
      this.formError = "";
      if (!this.validatePrizes()) {
        return;
      }
      if (!this.validateUrlLengths()) {
        return;
      }
      this.saving = true;
      try {
        this.ensureModeForType();
        this.persist();

        const startTimestamp = this.formatDateTime(this.form.date_start, this.form.date_start_time);
        const endDateForPersist = this.form.date_end || this.form.date_start;
        const endTimeForPersist = this.form.date_end_time || this.form.date_start_time;
        const endTimestamp = endDateForPersist ? this.formatDateTime(endDateForPersist, endTimeForPersist) : null;

        const payload = {
          name: this.form.name,
          country_id: this.form.country_id,
          type_id: this.form.type_id,
          flow_scope: this.form.flow_scope,
          is_qualification_dual: this.form.is_qualification_dual,
          is_vs: this.form.is_vs,
          round_size_id: this.form.round_size_id,
          is_open: this.form.is_open,
          gender: this.form.gender,
          category: this.form.category,
          rule_id: this.form.rule_id,
          tricklist_version_id: this.form.tricklist_version_id,
          access_link: this.form.access_link,
          location: this.form.location,
          url_event: this.form.url_event,
          url_social: this.form.url_social,
          fees: this.form.fees,
          prize_amount_total: this.form.prize_amount_total,
          prize_amount_first: this.form.prize_amount_first,
          prize_amount_second: this.form.prize_amount_second,
          prize_amount_third: this.form.prize_amount_third,
          prize_amount_fourth: this.form.prize_amount_fourth,
          prize_split_count: this.form.prize_split_count,
          img_path: this.form.img_path,
          date_start: startTimestamp,
          date_end: endTimestamp
        };

        if (this.competitionIdToEdit) {
          await this.$axios.put(`/competitions/${this.competitionIdToEdit}`, { formUpdate: payload }, {
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          });
        } else {
          await this.$axios.post("/competitions/store", { formCreate: payload }, {
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          });
        }
        this.clearDraftStorage();
        this.hasUnsavedChanges = false;
        this.$router.push("/competitions");
      } catch (error) {
        console.error("Error al guardar la competición desde el asistente", error);
      } finally {
        this.saving = false;
      }
    },
    async save() {
      this.formError = "";
      if (!this.validatePrizes()) {
        return;
      }
      if (!this.validateUrlLengths()) {
        return;
      }
      if (!this.form.name || !this.form.date_start || !this.form.country_id) {
        this.formError = this.$t("competitions.fill_fields");
        return;
      }
      this.saving = true;
      try {
        this.ensureModeForType();
        this.persist();

        const startTimestamp = this.formatDateTime(this.form.date_start, this.form.date_start_time);
        const endDateForPersist = this.form.date_end || this.form.date_start;
        const endTimeForPersist = this.form.date_end_time || this.form.date_start_time;
        const endTimestamp = this.formatDateTime(endDateForPersist, endTimeForPersist);

        const payload = {
          name: this.form.name,
          country_id: this.form.country_id,
          type_id: this.form.type_id,
          flow_scope: this.form.flow_scope,
          is_qualification_dual: this.form.is_qualification_dual,
          is_vs: this.form.is_vs,
          round_size_id: this.form.round_size_id,
          is_open: this.form.is_open,
          gender: this.form.gender,
          category: this.form.category,
          rule_id: this.form.rule_id,
          tricklist_version_id: this.form.tricklist_version_id,
          access_link: this.form.access_link,
          location: this.form.location,
          url_event: this.form.url_event,
          url_social: this.form.url_social,
          fees: this.form.fees,
          prize_amount_total: this.form.prize_amount_total,
          prize_amount_first: this.form.prize_amount_first,
          prize_amount_second: this.form.prize_amount_second,
          prize_amount_third: this.form.prize_amount_third,
          prize_amount_fourth: this.form.prize_amount_fourth,
          prize_split_count: this.form.prize_split_count,
          img_path: this.form.img_path,
          date_start: startTimestamp,
          date_end: endTimestamp
        };

        if (this.competitionIdToEdit) {
          await this.$axios.put(`/competitions/${this.competitionIdToEdit}`, { formUpdate: payload }, {
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          });
        } else {
          await this.$axios.post("/competitions/store", { formCreate: payload }, {
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          });
        }
        this.clearDraftStorage();
        this.hasUnsavedChanges = false;
        this.$router.push("/competitions");
      } catch (error) {
        console.error("Error al guardar la competición desde el asistente", error);
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push("/competitions");
    },

    validateUrlLengths() {
      const limit = 255;
      this.urlErrors = {
        url_event: "",
        url_social: "",
        location: "",
      };

      const fields = [
        { key: "url_event", label: "Event" },
        { key: "url_social", label: "Social" },
        { key: "location", label: "Location" },
      ];

      let hasError = false;
      for (const field of fields) {
        const value = this.form[field.key];
        if (value && String(value).length > limit) {
          const msg = this.$t("competitions.assistant.messages.url_too_long", { limit }) ||
            `La URL de ${field.label} supera ${limit} caracteres. Usa una versión más corta.`;
          this.urlErrors = { ...this.urlErrors, [field.key]: msg };
          if (!hasError) {
            this.formError = msg;
          }
          hasError = true;
        }
      }

      if (hasError) return false;
      this.formError = "";
      return true;
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 900px;
  margin: 20px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.header-sticky {
  position: sticky;
  top: 0;
  z-index: 8;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(6px);
  padding: 14px 18px 12px;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  color: #5b5b5b;
  margin: 0;
}
.lead {
  margin: 6px 0 0;
  color: #444;
}
.progress-wrapper {
  min-width: 260px;
  flex: 1;
}
.progress-label {
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}
.progress-bar-outer {
  height: 10px;
  background: #f0f0f0;
  border-radius: 999px;
  overflow: hidden;
}
.progress-bar-inner {
  height: 100%;
  background: linear-gradient(90deg, #4c56af, #28a745);
  transition: width 0.2s ease;
}
.checklist {
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 6px;
  color: #555;
}
.checklist li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}
.checklist li.done {
  color: #2f855a;
}
.card {
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  overflow: hidden;
}
.card-header {
  padding: 14px 16px 0;
}
.card-sub {
  margin-top: 10px;
}
.card-sub {
  color: #666;
  margin: 6px 0 0;
}
.card-body {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.card-body > .field:first-of-type {
  margin-top: 12px; /* breathing room below subtitle before first label */
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}
.field-grid-3 {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}
label {
  font-weight: 600;
  color: #333;
}
.helper-text {
  margin-top: 8px;
  color: #666;
  font-size: 13px;
}
.percentage-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e9f3ff;
  color: #0b66c3;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.image-upload {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}
.cropper-wrapper {
  width: 100%;
  max-width: 560px;
}
.image-preview {
  width: 240px;
  height: 135px;
  object-fit: cover;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f9f9f9;
}
.image-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.preview-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #4c56af;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}
.upload-btn input {
  display: none;
}
.btn-clear {
  border: 1px solid #ccc;
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #333;
}
.btn-clear:hover {
  background: #f2f2f2;
}
.error-text {
  color: #c53030;
  font-weight: 600;
  margin-top: 4px;
  font-size: 13px;
}
input, select {
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px;
  font-size: 14px;
}
.type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.type-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
}
.type-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.type-card.active {
  border-color: #4c56af;
  box-shadow: 0 6px 18px rgba(76, 86, 175, 0.15);
  background: #fff;
}
.type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pill {
  background: #eef1ff;
  color: #4c56af;
  padding: 4px 8px;
  border-radius: 999px;
  font-weight: 600;
}
.type-description {
  margin: 8px 0 0;
  color: #444;
}
.type-hint {
  margin: 8px 0 0;
  color: #777;
}
.preview-vs {
  font-weight: 800;
  color: #ff0000;
  padding: 0 6px;
  font-size: 18px;
}
.preview-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 4px;
}
.preview-tab {
  border: 1px solid #cfd5ff;
  background: #f4f6ff;
  color: #304286;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}
.preview-tab.active {
  background: #4c56af;
  color: #fff;
  border-color: #4c56af;
  box-shadow: 0 6px 12px rgba(76, 86, 175, 0.18);
}
.preview-tab:hover:not(.active) {
  background: #e8ebff;
}
.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.actions-right {
  display: flex;
  gap: 8px;
}
.btn-primary, .btn-secondary {
  border: none;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
}
.btn-primary {
  background: #4c56af;
  color: #fff;
}
.btn-secondary {
  background: #f4f4f4;
  color: #333;
}
.btn-validate {
  border: none;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  background: #2f855a;
  color: #fff;
}
.btn-validate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-small {
  padding: 8px 10px;
  font-size: 13px;
}
.btn-primary:disabled, .btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.spinner {
  border: 2px solid #fff;
  border-top: 2px solid rgba(255,255,255,0.2);
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: inline-block;
  margin-right: 6px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.fade-step-enter-active, .fade-step-leave-active {
  transition: all 0.2s ease;
}
.fade-step-enter-from, .fade-step-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
.rule-link {
  color: #4c56af;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.rule-link:hover {
  text-decoration: underline;
}
@media (max-width: 768px) {
  .header-hidden-mobile {
    transform: translateY(-100%);
    opacity: 0;
    pointer-events: none;
  }
  .preview-card-mobile-hide {
    display: none !important;
  }
}
@media (max-width: 640px) {
  .header { flex-direction: column; }
  .progress-wrapper { width: 100%; }
  .header-sticky {
    padding: 12px 14px 10px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
  }
}
</style>
